import * as React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { PAGE_GUTTER } from '../../core/theme/layout';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  /** Optional leading element (e.g. a status pill), rendered after the title. */
  leading?: React.ReactNode;
  /**
   * Przycisk "wroc", renderowany NA LEWO od tytulu. Wczesniej kazdy ekran
   * wstawial go we wlasnym `<Box>` nad naglowkiem, powielajac ten sam `px`, zeby
   * trzymal linie tytulu — i zjadajac osobny wiersz nad kazda podstrona.
   */
  back?: React.ReactNode;
  /**
   * Rozciaga blok akcji na cala szerokosc na telefonie. Opt-in, a nie domyslne
   * zachowanie, bo pozostale ekrany trzymaja w `actions` male przyciski tekstowe
   * albo FAB-a (position: fixed), ktorym rozciaganie nic nie daje.
   */
  stretchActionsOnMobile?: boolean;
}

/**
 * Strzalka "wroc" jest wysrodkowana wzgledem wiersza z tytulem, a jego wysokosc
 * zalezy od zawartosci: sam tytul (~36px), tytul + podtytul (~65px), logo
 * restauracji w `back` na ekranie produktow (72px). Bez stalej wysokosci strzalka
 * skakala miedzy ekranami. Minimum rowne najwyzszej zawartosci trzyma ja w tej
 * samej osi, nie dokladajac pustego miejsca pod naglowkiem.
 * md: logo 72px. xs: logo jest ukryte, tytul 24px*1.2 + odstep 8px + podtytul 14px*1.5.
 */
const TITLE_ROW_MIN_HEIGHT = { xs: 58, md: 72 };

/**
 * Consistent page header used across list/management screens.
 * - Generous whitespace, strong type hierarchy.
 * - Surfaces primary CTAs on the right; secondary actions can be grouped by caller.
 * - Responsive: stacks on mobile, the page-title block stays left-aligned.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  leading,
  back,
  stretchActionsOnMobile = false,
}) => {
  return (
    <Box
      sx={{
        pt: { xs: 'var(--space-8)', md: 'var(--space-10)' },
        pb: { xs: 'var(--space-5)', md: 'var(--space-6)' },
        px: PAGE_GUTTER,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        flexWrap: { sm: 'wrap' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 'var(--space-3)', sm: 'var(--space-4)' },
      }}
    >
      {/* Animacja siedzi na bloku z tytulem, NIE na calym naglowku. `ms-fade-in`
          animuje `transform`, a element z niezerowym transformem jest containing
          blockiem dla `position: fixed` — mobilny FAB z `actions` pozycjonowal sie
          wtedy wzgledem naglowka i po zakonczeniu animacji przeskakiwal do rogu
          viewportu. */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, minHeight: TITLE_ROW_MIN_HEIGHT }}>
        {back}

        <Stack className="ms-fade-in" spacing={1} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography
              component="h2"
              sx={{
                m: 0,
                fontSize: { xs: 'var(--text-2xl)', md: 'var(--text-3xl)' },
                fontWeight: 800,
                letterSpacing: 'var(--tracking-tight)',
                lineHeight: 'var(--leading-tight)',
                color: 'var(--color-text-primary)',
              }}
            >
              {title}
            </Typography>
            {leading}
          </Stack>
          {subtitle && (
            <Typography
              sx={{
                fontSize: 'var(--text-base)',
                color: 'var(--color-text-tertiary)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
      </Stack>

      {actions && (
        <Stack
          direction={stretchActionsOnMobile ? { xs: 'column', sm: 'row' } : 'row'}
          spacing={1.5}
          useFlexGap
          alignItems={stretchActionsOnMobile ? { xs: 'stretch', sm: 'center' } : 'center'}
          sx={{
            flexShrink: 0,
            flexWrap: 'wrap',
            maxWidth: '100%',
            ...(stretchActionsOnMobile && {
              alignSelf: { xs: 'stretch', sm: 'auto' },
              '& > *': { width: { xs: '100%', sm: 'auto' } },
            }),
          }}
        >
          {actions}
        </Stack>
      )}
    </Box>
  );
};

export default PageHeader;
